/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/utils/attendanceTiming.ts
 * PURPOSE: Pure clock-in timing warnings (late-for-shift-start / early-before-
 *          tolerance) — web sync of mobile PR #603 (BS-300 §1bis G2 + G5).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-12
 * COMPLIANCE: SDLC 6.5.0 (mobile↔web behavior parity)
 * PILLAR: P1 (Stage 06 - BUILD)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @business-context
 * - Domain: Attendance, Vietnamese Labor Law-adjacent shift punctuality
 * - Impact: Employee UX — non-blocking warnings only (banner, không chặn xác nhận)
 * - Stakeholders: Employees, Branch Managers
 *
 * @technical-architecture
 * - Layer: Pure utility (no side effects, no React)
 * - Pattern: Ported 1:1 from mobile/src/screens/attendance/AttendanceScreen.tsx
 *   (`todayTimeToEpoch` + `clockInLateInfo` + `clockInEarlyInfo`, commit
 *   84d2a5abf / PR #603) so the two platforms fire on the same thresholds.
 *
 * @cross-references
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx
 * - Mobile config: mobile/src/config/attendance.config.ts (SHIFT_TIMING)
 * - Backend tolerance source: backend/app/api/v1/gps_attendance.py
 *   (GET /api/v1/gps-attendance/branches/locations → early_clockin_tolerance_minutes,
 *   reads tenant_workforce_configs.workforce_settings->'attendance'->>
 *   'early_clockin_tolerance_minutes', default 30)
 * - Consumers: frontend/src/features/attendanceModern/useAttendanceModernClock.ts,
 *   frontend/src/pages/AttendanceModern.tsx
 * - Tests: frontend/tests/utils/attendanceTiming.test.ts
 */

/** Mirrors mobile SHIFT_TIMING.LATE_FLAG_THRESHOLD (attendance.config.ts:126). */
export const LATE_FLAG_THRESHOLD_MINUTES = 15;

/** Mirrors mobile SHIFT_TIMING.EARLY_CLOCK_IN_TOLERANCE (attendance.config.ts:122) —
 * used only as a client-side fallback before the tenant-configured value
 * (`early_clockin_tolerance_minutes`) has loaded from the backend. */
export const DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES = 30;

export interface ClockInTimingWarning {
  isLate: boolean;
  lateMinutes: number;
  isEarly: boolean;
  earlyMinutes: number;
}

const NO_WARNING: ClockInTimingWarning = {
  isLate: false,
  lateMinutes: 0,
  isEarly: false,
  earlyMinutes: 0,
};

/**
 * Combine TODAY's calendar date (from `now`) with a "HH:MM[:SS]" time-of-day
 * string into an epoch millis. Ported from mobile's `todayTimeToEpoch` —
 * deliberately ignores the shift's own `date` field and always anchors on
 * "now"'s local calendar day, since callers only ever pass in *today's* shift.
 */
function todayTimeToEpoch(hhmmss: string | null | undefined, now: Date): number | null {
  if (!hhmmss) return null;
  const parts = hhmmss.split(':').map((p) => parseInt(p, 10));
  if (parts.length < 2 || parts.some((p) => Number.isNaN(p))) return null;
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parts[0], parts[1], parts[2] ?? 0);
  return d.getTime();
}

/**
 * BS-300 §1bis G2 (late-for-shift-start) + G5 (early-before-tolerance).
 * Non-blocking — caller renders this as a warning banner only, never disables
 * the confirm action (parity with mobile AttendanceScreen.tsx clockInLateInfo
 * / clockInEarlyInfo, PR #603).
 *
 * @param shiftStartTime "HH:MM:SS" (or "HH:MM") — Schedule.start_time for today's shift, or null/undefined if no shift.
 * @param earlyToleranceMinutes tenant-configured tolerance (from `early_clockin_tolerance_minutes`); defaults to 30 if not yet loaded.
 * @param now injectable for tests; defaults to `new Date()`.
 */
export function computeClockInTimingWarning(
  shiftStartTime: string | null | undefined,
  earlyToleranceMinutes: number = DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES,
  now: Date = new Date(),
): ClockInTimingWarning {
  const shiftStartEpoch = todayTimeToEpoch(shiftStartTime, now);
  if (shiftStartEpoch === null) return NO_WARNING;

  const diffMin = Math.round((now.getTime() - shiftStartEpoch) / 60000);

  if (diffMin > LATE_FLAG_THRESHOLD_MINUTES) {
    return { isLate: true, lateMinutes: diffMin, isEarly: false, earlyMinutes: 0 };
  }

  const minutesBeforeShift = -diffMin;
  if (minutesBeforeShift > earlyToleranceMinutes) {
    return { isLate: false, lateMinutes: 0, isEarly: true, earlyMinutes: minutesBeforeShift };
  }

  return NO_WARNING;
}

export interface ClockOutTimingWarning {
  isLate: boolean;
  lateMinutes: number;
}

const NO_CLOCK_OUT_WARNING: ClockOutTimingWarning = { isLate: false, lateMinutes: 0 };

/**
 * SC-017-EMP-ATTENDANCE [9] end_verify (2026-09-02) — trễ >15 phút so giờ KẾT
 * THÚC ca (khác `computeClockInTimingWarning`, tính so giờ BẮT ĐẦU). Cùng
 * ngưỡng `LATE_FLAG_THRESHOLD_MINUTES` với mobile (`AttendanceScreen.tsx`
 * `lateInfo`, `attendance.config.ts:126`). Web trước đây KHÔNG có phép tính
 * này — clock-out không có khái niệm "trễ giờ" nào cả.
 *
 * @param shiftEndTime "HH:MM:SS" (hoặc "HH:MM") — Schedule.end_time của ca hôm nay.
 */
export function computeClockOutTimingWarning(
  shiftEndTime: string | null | undefined,
  now: Date = new Date(),
): ClockOutTimingWarning {
  const shiftEndEpoch = todayTimeToEpoch(shiftEndTime, now);
  if (shiftEndEpoch === null) return NO_CLOCK_OUT_WARNING;

  const diffMin = Math.round((now.getTime() - shiftEndEpoch) / 60000);
  if (diffMin > LATE_FLAG_THRESHOLD_MINUTES) {
    return { isLate: true, lateMinutes: diffMin };
  }
  return NO_CLOCK_OUT_WARNING;
}
